<script setup>
import BaseInputLabeled from "@/Components/UI/inputs/BaseInputLabeled.vue";
import { computed, ref, watch } from "vue";

const props = defineProps({
    modelValue: {
        type: String,
        required: true,
    },
    label: {
        type: String,
        required: true,
    },
    // Error MESSAGE — forwarded to BaseInputLabeled's FormError.
    error: String,
    // Canonical validity FLAG — forwarded; BaseInputLabeled derives from `error` when omitted.
    invalid: {
        type: Boolean,
        default: undefined,
        validator: (v) => typeof v === "boolean",
    },
    required: Boolean,
});

defineOptions({
    inheritAttrs: false,
});

const emit = defineEmits(["update:modelValue"]);

// Single input, dynamic mask — no component swap, so no focus jump / remount.
// A leading "+" is the only unambiguous phone signal: an email never starts with
// "+", but it CAN start with a digit (e.g. 1user@x.ru), so a bare digit must NOT
// trigger the mask or those addresses become untypeable. Phones typed without
// "+" (8…, 9…) still log in unformatted — the backend normalizes them. An empty
// data-maska string is treated by maska as "no mask" (free text for email).
const phoneMask = "+7 (###) ###-##-##";
const looksLikePhone = (v) => (v ?? "").startsWith("+");

// display = what the user sees (maska formats it in place); the parent gets the
// canonical value: "+7" + 10 digits for a phone (regex /^\+7\d{10}$/), raw text
// for an email. The "+7" prefix of the mask is a literal, so the masked digits
// already include the leading 7 — "+" + all digits reproduces +7XXXXXXXXXX.
const display = ref(props.modelValue ?? "");
const isPhone = computed(() => looksLikePhone(display.value));
const currentMask = computed(() => (isPhone.value ? phoneMask : ""));

const toCanonical = (text) => {
    if (!looksLikePhone(text)) {
        return text ?? "";
    }
    const digits = (text ?? "").replace(/\D/g, "");
    return digits ? "+" + digits : "+";
};

// Keep the display in sync when the parent resets/prefills (e.g. form clear).
watch(
    () => props.modelValue,
    (next) => {
        if (toCanonical(display.value) !== next) {
            display.value = next ?? "";
        }
    },
);

const onInput = (val) => {
    display.value = val ?? "";
    emit("update:modelValue", toCanonical(display.value));
};
</script>

<template>
    <BaseInputLabeled
        v-bind="$attrs"
        :required="required"
        :model-value="display"
        :label="label"
        :inputmode="isPhone ? 'tel' : 'email'"
        :error="error"
        :invalid="invalid"
        :data-maska="currentMask"
        v-maska
        @update:model-value="onInput" />
</template>
